import { AloftEnum, lens as L, isDefined, noop } from '@aloft/toolkit-shared';
import { Input, Select } from '@aloft/toolkit-sui';

import { ReactNode, useState } from 'react';

import { Td, Tr } from '../../../../components/table.tsx';
import { MatchCriteria, MatchCriteriaItem, MatchProperty } from '../../matching/types.ts';

const matchTypesNonNumeric: Set<MatchType> = new Set(['any', 'equal']);
const matchTypesSmallCount: Set<MatchType> = new Set([
  'any',
  'equal',
  'less',
  'greater',
  'withinUnit',
]);
const matchTypesNumeric: Set<MatchType> = new Set([
  'any',
  'equal',
  'less',
  'greater',
  'withinUnit',
  'withinPercent',
]);
const matchTypesDate: Set<MatchType> = new Set(['any', 'equal', 'withinUnit']);
const matchTypesProximity: Set<MatchType> = new Set(['any', 'less', 'greater']);

type MatchType = MatchCriteriaItem['t'];

type CriteriaRowInfo = {
  label: string;
  types: Set<MatchType>;
  unit?: string;
};

const CriteriaRows = AloftEnum<CriteriaRowInfo>()({
  glaSqFt: { label: 'GLA', unit: 'Sqft', types: matchTypesNumeric },
  basementSqFt: { label: 'Basement', unit: 'Sqft', types: matchTypesNumeric },
  siteSizeSqFt: { label: 'Site Size', unit: 'Sqft', types: matchTypesNumeric },
  yearBuilt: { label: 'Year Built', unit: 'Years', types: matchTypesNumeric },
  contractDate: { label: 'Contract Date', unit: 'Days', types: matchTypesDate },
  saleDate: { label: 'Sale Date', unit: 'Days', types: matchTypesDate },
  coords: { label: 'Proximity', types: matchTypesProximity },
  zipCode: { label: 'Zip Code', types: matchTypesNonNumeric },
  salePrice: { label: 'Sale Price', unit: '$', types: matchTypesNumeric },
  view: { label: 'View', types: matchTypesNonNumeric },
  designStyle: { label: 'Design/Style', types: matchTypesNonNumeric },
  heatingCooling: { label: 'Heating/Cooling', types: matchTypesNonNumeric },
  hasBasement: { label: 'Has Basement', types: matchTypesNonNumeric },
  hasAttachment: { label: 'Has Attachment', types: matchTypesNonNumeric },
  bedrooms: { label: 'Bedrooms', types: matchTypesSmallCount },
  bathrooms: { label: 'Bathrooms', types: matchTypesSmallCount },
  halfBathrooms: { label: 'Half Bathrooms', types: matchTypesSmallCount },
  threeQuarterBathrooms: { label: '3/4 Bathrooms', types: matchTypesSmallCount },
  totalBathrooms: { label: 'Total Bathrooms', types: matchTypesSmallCount },
  stories: { label: 'Stories', types: matchTypesSmallCount },
  attachedGarageParkingSpacesCount: { label: 'Garage Spaces', types: matchTypesSmallCount },
  propertyType: { label: 'Property Type', types: matchTypesNonNumeric },
  propertySubType: { label: 'Property Subtype', types: matchTypesNonNumeric },
}) satisfies Partial<Record<keyof MatchProperty, CriteriaRowInfo>>;

type MatchRowFormState = {
  t: MatchType;
  numeric: number | undefined;
  percent: number | undefined;
};

const formStateLens: L.Lens<MatchCriteriaItem | undefined, MatchRowFormState> = new L.Lens(
  (item) => ({
    t: item?.t ?? 'any',
    numeric: item?.t === 'withinPercent' ? undefined : item?.value,
    percent: item?.t === 'withinPercent' ? item?.value : undefined,
  }),
  (item, state): MatchCriteriaItem | undefined => {
    switch (state.t) {
      case 'any':
      case 'equal':
        return { t: state.t };
      case 'greater':
      case 'less':
      case 'withinUnit': {
        const value = state.numeric ?? item?.value;
        return value === undefined ? undefined : { t: state.t, value };
      }
      case 'withinPercent': {
        const value = state.percent ?? item?.value;
        return value === undefined ? undefined : { t: 'withinPercent', value };
      }
      default:
        return undefined;
    }
  },
);

export const DefineTheMatchInputs = (props: {
  id: keyof MatchProperty;
  criteria: MatchCriteria;
  setCriteria: (criteria: MatchCriteria) => void;
}) => {
  const { id, criteria, setCriteria } = props;
  if (!CriteriaRows._isKey(id)) {
    console.error(`Invalid key: ${id}`);
    return null;
  }

  return (
    <Row
      label={CriteriaRows[id].label}
      unit={CriteriaRows[id].unit}
      matchTypes={CriteriaRows[id].types}
      criteria={criteria}
      setCriteria={setCriteria}
      lens={L.propertyLens(id)}
    />
  );
};

const Row = (props: {
  label: string;
  unit?: string;
  matchTypes: Set<MatchType>;
  criteria: MatchCriteria;
  setCriteria: (criteria: MatchCriteria) => void;
  lens: L.Lens<MatchCriteria, MatchCriteriaItem | undefined>;
}) => {
  const { label, unit, matchTypes, criteria, setCriteria, lens } = props;

  const [item, setItem] = lens.adaptReact(criteria, setCriteria);
  const [formState, setFormStateReal] = useState(formStateLens.get(item));

  const setFormState = (state: MatchRowFormState): void => {
    setFormStateReal(state);
    setItem(formStateLens.set(item, state));
  };

  const { t: matchType, numeric, percent } = formState;
  const setMatchType = (t: MatchType) => setFormState({ ...formState, t });
  const setNumeric = (n?: number) => setFormState({ ...formState, numeric: n });
  const setPercent = (p?: number) => setFormState({ ...formState, percent: p });

  const error = item === undefined ? 'This field is required' : undefined;

  const select = (
    <Select
      size="xs"
      options={matchTypesToOptions(matchTypes, unit ?? label)}
      value={matchType}
      onChange={(value) => setMatchType(value ?? 'any')}
      __dangerDoNotUseSx={{
        backgroundColor: 'textOffset',
      }}
      placeholder=" "
    />
  );

  const valueInput = (
    <Input
      type="number"
      size="xs"
      value={numeric}
      onChange={(x) => (x === '' ? noop() : setNumeric(x))}
      onClear={() => setNumeric(undefined)}
      errorText={error}
    />
  );
  const percentInput = (
    <Input
      type="percent"
      size="xs"
      value={percent === undefined ? undefined : percent * 100}
      onChange={(v) => setPercent(v / 100)}
      onClear={() => setPercent(undefined)}
    />
  );

  let inputColumnContent: ReactNode;
  if (matchType === 'less' || matchType === 'greater' || matchType === 'withinUnit') {
    inputColumnContent = valueInput;
  } else if (matchType === 'withinPercent') {
    inputColumnContent = percentInput;
  }

  return (
    <Tr>
      <Td width="150px" type="editable">
        {select}
      </Td>
      <Td width="150px" type={isDefined(inputColumnContent) ? 'editable' : 'readonly'}>
        {inputColumnContent}
      </Td>
    </Tr>
  );
};

const matchTypesToOptions = (
  matchTypes: Set<MatchType>,
  unit: string,
): { value: MatchType; label: string }[] => {
  const options: { value: MatchType; label: string }[] = [];

  if (matchTypes.has('any')) {
    options.push({ value: 'any', label: 'Ignore' });
  }
  if (matchTypes.has('equal')) {
    options.push({ value: 'equal', label: 'Perfect Match' });
  }
  if (matchTypes.has('less')) {
    options.push({ value: 'less', label: 'Less Than' });
  }
  if (matchTypes.has('greater')) {
    options.push({ value: 'greater', label: 'Greater Than' });
  }
  if (matchTypes.has('withinUnit')) {
    options.push({ value: 'withinUnit', label: `Within +/- ${unit}` });
  }
  if (matchTypes.has('withinPercent')) {
    options.push({ value: 'withinPercent', label: 'Within +/- Percent' });
  }

  return options;
};
